import {Button, Checkbox, Divider, Dropdown, Menu} from "antd"
import {useToggle} from "react-use"
import cx from "classnames"
import {map} from "lodash"
import {useEdularModulesContext, Modules} from "@edular/modules"
import {calendarIntegrationService} from "services"
import {useModel, useMutation} from "hooks"
import {handleError} from "helpers"
import {
    ExternalCalendarProvider,
    GenerateExternalCalendarOAuthLinkParams,
    LinkedCalendarInfo,
    StaticOption
} from "types/calendar"
import {Icon} from "components/Icon"
import googleLogoImg from "assets/images/google-logo.svg"
import microsoftLogoImg from "assets/images/microsoft-logo.svg"
import styles from "./ConnectedCalendars.module.css"

type Props = {
    linkedCalendars: LinkedCalendarInfo[]
    staticOptions: StaticOption[]
    updateLinkedCalendars: (data: LinkedCalendarInfo[]) => void
    updateStaticOptions: (data: StaticOption[]) => void
}

export const ConnectedCalendars = ({
    linkedCalendars,
    staticOptions,
    updateLinkedCalendars,
    updateStaticOptions
}: Props) => {
    const {isModuleEnable} = useEdularModulesContext()
    const providers: {id: ExternalCalendarProvider; label: string}[] = [
        isModuleEnable(Modules.OutlookCalendar) && {id: ExternalCalendarProvider.Outlook, label: "Outlook"},
        isModuleEnable(Modules.GoogleCalendar) && {id: ExternalCalendarProvider.Google, label: "Google"}
    ].filter(Boolean)
    const [showContent, toggleShowContent] = useToggle(true)
    const model = useModel()

    const generateOAuthMutation = useMutation(
        (data: GenerateExternalCalendarOAuthLinkParams) => calendarIntegrationService.generateOAuth(data),
        {
            onSuccess: ({authUrl}) => window.open(authUrl),
            onError: (error) => handleError(error)
        }
    )
    const disconnectMutation = useMutation(
        (data: {calendarId: number}) => calendarIntegrationService.disconnectUserCalendars(data),
        {
            onSuccess: (_, payload) => {
                const newLinkedCalendar = linkedCalendars.filter((item) => item.id !== payload.calendarId)
                updateLinkedCalendars(newLinkedCalendar)
            },
            onError: (error) => handleError(error)
        }
    )

    const toggleLinkCalendar = (data: LinkedCalendarInfo, checked: boolean) => {
        const newLinkedCalendars = linkedCalendars.map((item) => {
            if (item.id === data.id && item.isPrimary === data.isPrimary) {
                return {...item, isShow: checked}
            }
            return item
        })
        updateLinkedCalendars(newLinkedCalendars)
    }

    const toggleStaticOption = (data: StaticOption, checked: boolean) => {
        const newStaticOptions = staticOptions.map((item) => {
            if (item.id === data.id) {
                return {...item, isShow: checked}
            }
            return item
        })
        updateStaticOptions(newStaticOptions)
    }

    const menu = (
        <Menu className={styles.menuActionWrap}>
            {providers.map((provider, index) => (
                <Menu.Item
                    key={index}
                    className={styles.menuActionItem}
                    onClick={() =>
                        generateOAuthMutation.mutate({provider: provider.id, successRedirect: window.location.href})
                    }>
                    {provider.label}
                </Menu.Item>
            ))}
        </Menu>
    )

    const renderLinkedCalendarInfo = (item: LinkedCalendarInfo) => {
        const {id, isPrimary, name, provider, isShow} = item

        return (
            <div key={id} className={styles.itemInfo}>
                <Checkbox checked={isShow} onChange={(event) => toggleLinkCalendar(item, event.target.checked)} />
                {provider && (
                    <img
                        src={provider === ExternalCalendarProvider.Google ? googleLogoImg : microsoftLogoImg}
                        className={styles.itemLogo}
                        alt="logo"
                    />
                )}
                <span className={styles.itemLabel}>{name}</span>
                {!isPrimary && !model.viewOtherUserCalendar && (
                    <Button
                        className={styles.actionBtn}
                        icon={<Icon icon="CLOSE" color="#000" className={styles.icon} />}
                        onClick={() => disconnectMutation.mutate({calendarId: id})}
                        disabled={disconnectMutation.isLoading}
                        loading={disconnectMutation.isLoading}
                    />
                )}
            </div>
        )
    }

    const renderStaticOption = (item: StaticOption) => {
        const {id, name, isShow} = item
        return (
            <div key={id} className={styles.itemInfo}>
                <Checkbox checked={isShow} onChange={(event) => toggleStaticOption(item, event.target.checked)} />
                <span className={styles.itemLabel}>{name}</span>
            </div>
        )
    }

    const renderContent = () => {
        if (!showContent) {
            return null
        }
        return (
            <div className={styles.content}>
                {map(linkedCalendars, (item) => renderLinkedCalendarInfo(item))}
                <Divider />
                {map(staticOptions, (item) => renderStaticOption(item))}
            </div>
        )
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <p className={styles.title}>Connected calendars</p>
                <div className={styles.actions}>
                    {!model.viewOtherUserCalendar && (
                        <Dropdown overlay={menu} trigger={["click"]}>
                            <Button
                                className={cx(styles.actionBtn, styles.actionBtn__add)}
                                icon={<Icon icon="PLUS" color="#000000d9" className={styles.icon} />}
                            />
                        </Dropdown>
                    )}
                    <Button
                        className={styles.actionBtn}
                        icon={
                            <Icon
                                icon="ARROW_DOWN"
                                color="#000000d9"
                                className={cx(styles.icon, {[styles.iconRotate]: !showContent})}
                            />
                        }
                        onClick={toggleShowContent}
                    />
                </div>
            </div>
            {renderContent()}
        </div>
    )
}
